// Components/PageTitle.js
import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO helper for per-route metadata.
 *
 * Props:
 * - title: string
 * - description?: string
 * - canonical?: string (absolute URL)
 * - robots?: "index,follow" | "noindex,nofollow" | string
 * - openGraph?: { title?, description?, image?, imageWidth?, imageHeight?, url?, type?, siteName? }
 * - twitter?: { card?, title?, description?, image? }
 * - structuredData?: object | object[]   // JSON-LD
 * - alternates?: Array<{ hrefLang: string, href: string }>  // hreflang
 * - prev?: string   // absolute URL of previous page (pagination)
 * - next?: string   // absolute URL of next page (pagination)
 */
const PageTitle = ({
  title,
  description,
  canonical,
  robots,
  openGraph = {},
  twitter = {},
  structuredData,
  alternates = [],
  prev,
  next,
}) => {
  const ogTitle = openGraph.title || title;
  const ogDesc = openGraph.description || description;
  const ogUrl = openGraph.url || canonical;
  const ogType = openGraph.type || "website";
  const ogImage = openGraph.image;
  const ogSiteName = openGraph.siteName || "Blitz";
  const ogImgW = openGraph.imageWidth;
  const ogImgH = openGraph.imageHeight;

  const twCard = twitter.card || "summary_large_image";
  const twTitle = twitter.title || title;
  const twDesc = twitter.description || description;
  const twImage = twitter.image || ogImage;

  const ldArray = Array.isArray(structuredData)
    ? structuredData
    : structuredData
    ? [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {robots && <meta name="robots" content={robots} />}

      {/* hreflang alternates */}
      {alternates.map((alt) =>
        alt?.href && alt?.hrefLang ? (
          <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
        ) : null
      )}

      {/* Pagination (for lists) */}
      {prev && <link rel="prev" href={prev} />}
      {next && <link rel="next" href={next} />}

      {/* OpenGraph */}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={ogSiteName} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImgW && <meta property="og:image:width" content={String(ogImgW)} />}
      {ogImgH && <meta property="og:image:height" content={String(ogImgH)} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twCard} />
      {twTitle && <meta name="twitter:title" content={twTitle} />}
      {twDesc && <meta name="twitter:description" content={twDesc} />}
      {twImage && <meta name="twitter:image" content={twImage} />}

      {/* JSON-LD */}
      {ldArray.map((obj, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }}
        />
      ))}
    </Helmet>
  );
};

export default PageTitle;
